import React, { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Shield, CheckCircle2, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CHECK_DEFS = [
  { key: "rate_limit_login_enabled", label: "Rate limit login", critical: true,
    detail: (s) => `${s.rate_limit_login_threshold || 5} tentativi → ${s.rate_limit_login_cooldown_sec || 60}s di blocco` },
  { key: "anti_bot_challenge_enabled", label: "Challenge anti-bot login", critical: true,
    detail: (s) => s.anti_bot_provider_external ? "Math challenge + provider esterno (Turnstile/reCAPTCHA)" : "Math challenge + honeypot field (built-in)" },
  { key: "credentials_hidden_from_login", label: "Credenziali nascoste dalla login", critical: true,
    detail: () => "Nessun quick-login né label che esponga email/password" },
  { key: "audit_log_enabled", label: "Audit log immutabile", critical: true,
    detail: () => "Ogni login/azione admin/invio referto loggati con IP, timestamp UTC" },
  { key: "tenant_isolation_enabled", label: "Tenant isolation (doctor_id)", critical: true,
    detail: () => "Admin non vede mai dati clinici dei medici; ogni query scoped per doctor_id" },
  { key: "jwt_secret_set", label: "JWT secret configurato", critical: true,
    detail: () => "JWT_SECRET/SECRET_KEY env attivo" },
  { key: "backup_encryption_enabled", label: "Backup cifrato AES-256-GCM", critical: true,
    detail: () => "BACKUP_ENCRYPTION_KEY presente; PBKDF2-SHA256 (300k iter)" },
  { key: "backup_scheduler_enabled", label: "Backup scheduler attivo", critical: true,
    detail: (s) => `Job giornaliero APScheduler (${s.cors_environment === 'production' ? '03:30 Europe/Rome' : '03:30 Europe/Rome'})` },
  { key: "backup_cloud_storage_set", label: "Cloud storage backup configurato", critical: false, warningIfFalse: true,
    detail: (s) => s.backup_cloud_storage_set ? "Object storage S3-compat collegato" : "Backup locale OK ma cloud non configurato (vedi tab Backup → secret BACKUP_S3_*)" },
  { key: "brevo_api_key_valid", label: "Brevo (email/SMS) API valida", critical: false, warningIfFalse: true,
    detail: (s) => s.brevo_api_key_valid ? "API valida, sender configurato" : "Brevo non configurato — messaggi paziente non funzioneranno" },
  // CORS — logica dinamica in funzione dell'ambiente (production strict / dev open)
  { key: "cors_strict", label: "CORS allowed origins ristretti",
    critical: (s) => s.cors_is_production,         // critico solo in produzione
    warningIfFalse: (s) => !s.cors_is_production,  // info in dev/preview
    detail: (s) => {
      const lbl = s.cors_state_label || (s.cors_is_production ? "CORS produzione ristretto" : "Default open solo dev/preview");
      const top = (s.cors_origins || []).slice(0, 3).join(", ");
      const more = (s.cors_origins || []).length > 3 ? " …" : "";
      return `${lbl} · ${top}${more}`;
    },
    customStatus: (s) => s.cors_is_production ? s.cors_strict : true /* in dev: sempre verde */
  },
];

export default function SecurityPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/security/status");
      setStatus(data);
    } catch (e) {
      toast.error(apiErr(e, "Errore stato sicurezza"));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (!status) return (
    <div className="text-slate-500 py-8 text-center flex items-center justify-center gap-2" data-testid="admin-security-loading">
      <Loader2 size={16} className="animate-spin" /> Caricamento stato sicurezza…
    </div>
  );

  const items = CHECK_DEFS.map((def) => {
    const rawVal = status[def.key];
    // Permetti customStatus(s) per logiche dinamiche (es. CORS in produzione)
    const value = def.customStatus ? !!def.customStatus(status) : !!rawVal;
    const critical = typeof def.critical === "function" ? !!def.critical(status) : !!def.critical;
    const warningIfFalse = typeof def.warningIfFalse === "function" ? !!def.warningIfFalse(status) : !!def.warningIfFalse;
    return { ...def, critical, warningIfFalse, value, detailText: def.detail ? def.detail(status) : "" };
  });
  const okCount = items.filter((c) => c.value).length;
  const criticalFails = items.filter((c) => c.critical && !c.value).length;
  const overallOk = criticalFails === 0;

  return (
    <div className="space-y-4" data-testid="admin-security-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Shield size={20} className="text-brand-azure" />
          <h2 className="font-extrabold text-brand-navy text-xl">Sicurezza piattaforma</h2>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${status.cors_is_production ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-amber-100 text-amber-800 border border-amber-300"}`} data-testid="security-env-badge" title={`origins: ${(status.cors_origins||[]).join(", ")}`}>
            {status.cors_is_production ? "Produzione" : (status.cors_environment || "preview")}
          </span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${overallOk ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid="security-overall-badge">
            {okCount}/{items.length} attivi · {overallOk ? "OK" : `${criticalFails} critici da fixare`}
          </span>
        </div>
        <button onClick={refresh} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-brand-navy px-3 py-2 rounded-lg border border-[#E2E8F0]" data-testid="security-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((c) => {
          const isWarn = !c.value && !c.critical;
          const isFail = !c.value && c.critical;
          const tone = c.value ? "bg-emerald-50 border-emerald-200"
                              : isFail ? "bg-red-50 border-red-300"
                                       : "bg-amber-50 border-amber-200";
          const textTone = c.value ? "text-emerald-800" : isFail ? "text-red-800" : "text-amber-800";
          const Icon = c.value ? CheckCircle2 : AlertTriangle;
          const iconTone = c.value ? "text-emerald-700" : isFail ? "text-red-700" : "text-amber-700";
          return (
            <div key={c.key} className={`rounded-xl p-4 border ${tone}`} data-testid={`security-check-${c.key}`}>
              <div className="flex items-center gap-2">
                <Icon size={16} className={iconTone} />
                <div className={`font-bold text-sm ${textTone}`}>{c.label}</div>
                {isWarn && <span className="ml-auto text-[9px] uppercase font-bold tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">attenzione</span>}
                {isFail && <span className="ml-auto text-[9px] uppercase font-bold tracking-wider bg-red-200 text-red-900 px-1.5 py-0.5 rounded">critico</span>}
              </div>
              <div className="text-xs text-slate-600 mt-1">{c.detailText}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Privacy by design — policy attive</h3>
        <ul className="space-y-1 text-xs text-slate-700">
          {(status.privacy_by_design_notes || []).map((n, i) => (
            <li key={i} className="flex items-start gap-1.5" data-testid={`security-privacy-note-${i}`}>
              <CheckCircle2 size={11} className="text-emerald-600 mt-0.5 shrink-0" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="text-[10px] text-slate-500 italic">
        Stato verificato: {status.last_checked_at ? new Date(status.last_checked_at).toLocaleString("it-IT") : "—"}. Endpoint <code className="font-mono">GET /api/admin/security/status</code>. Non viene mai esposta nessuna chiave o segreto.
      </div>
    </div>
  );
}
